// SETTINGS V2 — Používatelia a prístup (Team) page (SETTINGS_V2_SP6 §5/§6/§7).
//
// Routed real page (route swap on useCustomerRoutes — `team`). Binds the reused
// customerUsersSlice + customerUsersApi (TWO base URLs: Customers roster+mutations,
// Account roles) UNCHANGED. Client-side 6-column sort via the imported legacy
// sortUsers (col 1 sorts userName — parity quirk). The shared `loading` flag
// blanks the whole table on ANY mutation (BINDING parity). Self delete/disable →
// accountContext.logout(), BOTH fulfilled-gated (SP10 F1 / Q-SETV2-23 fixed the
// legacy disable-self wart). Delete is the ONLY frontend-guarded action (kebab
// PermissionGuard(Users, Admin)). change-role stays dead (no UI). TWO ActionToast
// at page root (parity error surface). Page ROOT carries NO animation (SP2 toast-
// stacking rule). Inline styles + scoped CSS-var tokens; no MUI/sx/dt/Tailwind.
// SEC-REQ-2: fetch only in the mount effect.
import React, { useEffect, useMemo, useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import usePermission from "../../../../shared/hooks/usePermission";
import { Modules } from "../../../../core/models/PermissionModels";
import Loader from "../../../../shared/components/Loader";
import ActionToast from "../../../../shared/components/ActionToast";
import { getNotificationMessage } from "../../../../shared/utils/notificationUtils";
import { NotificationMessage } from "../../../../shared/models/models";
import accountContext from "../../../../account/accountContext";
import { User } from "../../models/userModels";
import { sortUsers } from "../../utils/employeeUtils";
import {
  getUsers,
  reinviteUser,
  pinRegenerate,
  enableUser,
  disableUser,
  deleteUser,
  setSort,
  clearError,
  clearSuccess,
} from "../../customerUsersSlice";
import { openDialog, closeDialog } from "../settingsV2UiSlice";
import { SCard } from "../atoms";
import SettingsV2ConfirmDialog from "../dialogs/SettingsV2ConfirmDialog";
import InvitePermanentDialogV2 from "../dialogs/InvitePermanentDialogV2";
import IssueGuestPassDialogV2 from "../dialogs/IssueGuestPassDialogV2";
import GuestPassListV2 from "./GuestPassListV2";
import TeamHeadV2 from "./TeamHeadV2";
import TeamRowV2 from "./TeamRowV2";

const TeamPageV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate: t } = useTranslation("settings");
  const { isPhone } = useBreakpoint();
  // Guest-pass issuance is a Machinery:Write operation (B6 §4.1) — Manager+Admin
  // see the menu action + list actions; Worker/Viewer don't.
  const { canWrite: canIssueGuestPass } = usePermission(Modules.Machinery);
  // Demo passes mint a Worker-privilege principal, so they sit behind Users:Admin
  // (DEMO_USER_TYPE_SPEC §7) — a stricter gate than the guest pass above.
  const { canAdmin: canMintDemoPass } = usePermission(Modules.Users);

  const { users, loading, success, error, sortBy, sortDesc } = useAppSelector(
    (s) => s.customerUsers
  );
  const openDialogId = useAppSelector((s) => s.settingsV2Ui.openDialog);
  const theme = useAppSelector((s) => s.settingsV2Ui.theme);

  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);

  const sortedUsers = useMemo(
    () => sortUsers(users, sortBy || 0, sortDesc),
    [users, sortBy, sortDesc]
  );

  // Mount fetch (parity).
  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  // Sort toggle (§6.5 — same col flips desc, new col resets asc).
  const handleSort = (parameter: number) => {
    if (sortBy === parameter) {
      dispatch(setSort({ sortBy: parameter, sortDesc: !sortDesc }));
    } else {
      dispatch(setSort({ sortBy: parameter, sortDesc: false }));
    }
  };

  // Row handlers (ported VERBATIM from SettingsTeam lines 104-172).
  const handleReinvite = async (user: User) => {
    await dispatch(reinviteUser({ userId: user.id }));
  };
  const handlePinRegenerate = async (user: User) => {
    await dispatch(pinRegenerate({ userId: user.id, visitPurpose: user.visitPurpose ?? "" }));
    await dispatch(getUsers());
  };
  const handleEnable = async (user: User) => {
    await dispatch(enableUser({ userId: user.id }));
    await dispatch(getUsers());
  };
  // §6.4 / Q-SETV2-23 (SP10 F1) — self-disable is now fulfilled-gated (mirrors
  // handleConfirmDelete below): a FAILED disable (network/403) no longer wipes the
  // session token or refetches. On success, self-target logs out then refetch.
  // FLAGGED deliberate behavior change vs the legacy wart.
  const handleDisable = async (user: User) => {
    const result = await dispatch(disableUser({ userId: user.id }));
    if (disableUser.fulfilled.match(result)) {
      if (user.id === accountContext.user?.id) {
        accountContext.logout();
      }
      await dispatch(getUsers());
    }
  };

  const handleAskDelete = (user: User) => {
    setDeleteTarget(user);
    dispatch(openDialog("deleteUser"));
  };
  const handleCancelDelete = () => {
    setDeleteTarget(null);
    dispatch(closeDialog());
  };
  // §6.2 — close first, then fulfilled-gated delete → self-logout → refetch.
  const handleConfirmDelete = async () => {
    const target = deleteTarget;
    dispatch(closeDialog());
    setDeleteTarget(null);
    if (!target) return;
    const result = await dispatch(deleteUser({ id: target.id }));
    if (deleteUser.fulfilled.match(result)) {
      if (target.id === accountContext.user?.id) {
        accountContext.logout();
      }
      await dispatch(getUsers());
    }
  };

  const rowHandlers = {
    onReinvite: handleReinvite,
    onPinRegenerate: handlePinRegenerate,
    onDisable: handleDisable,
    onEnable: handleEnable,
    onDelete: handleAskDelete,
  };

  // Invite ▾ menu (§5.8) — Radix, ungated UI (server enforces Users:Write).
  const inviteMenu = (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="btn btn-primary"
          style={{ background: "var(--mod-accent)", borderColor: "var(--mod-accent)", whiteSpace: "nowrap" }}
        >
          {t("settings:invite") as string} ▾
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={`settings-v2-root p-${theme} mod-nastavenia sv2-menu`}
          align="end"
          sideOffset={4}
        >
          <DropdownMenu.Item
            className="sv2-menu-item"
            onSelect={() => dispatch(openDialog("invitePermanent"))}
          >
            {t("settings:permanentEmployee") as string}
          </DropdownMenu.Item>
          {canIssueGuestPass && (
            <DropdownMenu.Item
              className="sv2-menu-item"
              onSelect={() => dispatch(openDialog("issueGuestPass"))}
            >
              {t("settings:guestPass.menuItem") as string}
            </DropdownMenu.Item>
          )}
          {/* DEMO_USER_TYPE_SPEC F1 — "add demo user". Opens the same mint dialog
              with the pass type pre-set to Demo. Admin-only in the UI, mirroring
              the server's RequireModulePermission(Users, Admin) on the demo mint. */}
          {canMintDemoPass && (
            <DropdownMenu.Item
              className="sv2-menu-item"
              onSelect={() => dispatch(openDialog("issueDemoPass"))}
            >
              {t("settings:guestPass.demoMenuItem") as string}
            </DropdownMenu.Item>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );

  const footnote = (
    <div
      style={{
        padding: "9px 16px",
        borderTop: "1px solid var(--border-soft)",
        background: "var(--bg-soft)",
        fontFamily: "var(--font-mono)",
        fontSize: 9.5,
        color: "var(--faint)",
        letterSpacing: "0.04em",
      }}
    >
      {t("settings:team.footnote") as string}
    </div>
  );

  let body: React.ReactNode;
  if (loading) {
    // Parity blanking: ANY mutation sets the shared loading → replaces header+rows.
    body = (
      <div style={{ display: "grid", placeItems: "center", padding: "48px 0" }}>
        <Loader />
      </div>
    );
  } else if (!users?.length) {
    body = (
      <div style={{ padding: "0 16px 16px" }}>
        <div
          style={{
            border: "1px dashed var(--border)",
            borderRadius: 8,
            padding: "32px 20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink)" }}>
            {t("settings:team.emptyTitle") as string}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--muted)" }}>
            {t("settings:team.emptyHint") as string}
          </div>
        </div>
      </div>
    );
  } else {
    body = (
      <>
        {!isPhone && (
          <TeamHeadV2 sortBy={sortBy} sortDesc={sortDesc} onSort={handleSort} t={t} />
        )}
        {isPhone ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              padding: "0 13px 13px",
              width: "100%",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            {sortedUsers.map((u, i) => (
              <TeamRowV2 key={u.id} user={u} index={i} {...rowHandlers} />
            ))}
          </div>
        ) : (
          <div style={{ width: "100%" }}>
            {sortedUsers.map((u, i) => (
              <TeamRowV2 key={u.id} user={u} index={i} {...rowHandlers} />
            ))}
          </div>
        )}
        {footnote}
      </>
    );
  }

  return (
    // Page root: NO animation/transform (SP2 §2 toast-stacking rule).
    <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0, flex: 1 }}>
      <ActionToast
        open={!!success}
        variant="success"
        message={getNotificationMessage(success, t as (key: string, params?: any) => string)}
        onClose={() => dispatch(clearSuccess(undefined))}
        duration={6000}
      />
      <ActionToast
        open={!!error}
        variant="error"
        message={t(NotificationMessage.error(error).message) as string}
        onClose={() => dispatch(clearError(undefined))}
        duration={6000}
      />

      <SCard
        title={t("settings:team.title") as string}
        hint={t("settings:team.description") as string}
        bleed
        action={inviteMenu}
        style={{ animation: "sv2-card-reveal 0.4s ease-out both" }}
      >
        {body}
      </SCard>

      <GuestPassListV2 />

      <InvitePermanentDialogV2 />
      <IssueGuestPassDialogV2 />

      <SettingsV2ConfirmDialog
        open={openDialogId === "deleteUser"}
        onClose={handleCancelDelete}
        title={t("settings:deleteUserTitle") as string}
        text={t("settings:deleteUserText", { userName: deleteTarget?.userName ?? "" }) as string}
        confirmLabel={t("settings:deleteUserConfirm") as string}
        cancelLabel={t("settings:deleteUserCancel") as string}
        danger
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default TeamPageV2;
